import { OnSiteApi } from "../apis/onsite";
import { Response } from "../models/response";
import { useState } from "react";
import HttpClient from "../utils/network";
import { AdminUser } from "../models/adminUser";
import { useAtom, useSetAtom } from "jotai";
import { userAtom } from "../atoms/userAtom";
import { SetSessionToken } from "../utils/store";
import { OtpVerifyToken } from "../models/otpVerifyToken";
import { otpVerifyTokenAtom } from "../atoms/loginAtom";
import { LoginType } from "../models/enum/login";
import { WeAuthRedirectUri } from "../models/weAuthRedirectUri";

export function useLogin() {
	const [isLoginPending, setIsLoginPending] = useState<boolean>(false);
	const setUser = useSetAtom(userAtom);
	const [otpVerifyToken, setOtpVerifyToken] = useAtom(otpVerifyTokenAtom);

	const oneDayLogin = async ({
		email,
		eventId,
	}: {
		email: string;
		eventId: number;
	}) => {
		setIsLoginPending(true);
		let response: Response<OtpVerifyToken> | null = null;
		try {
			response = new Response<OtpVerifyToken>(
				await HttpClient.request(
					OnSiteApi.oneDayStaffLogin({
						email: email,
						eventId: eventId,
					}),
				),
			);
			if (response?.data.verifyToken) {
				setOtpVerifyToken(response.data.verifyToken);
			}
		} finally {
			setIsLoginPending(false);
		}
		return response;
	};

	const weAuthLogin = async () => {
		setIsLoginPending(true);
		const response = new Response<WeAuthRedirectUri>(
			await HttpClient.request(OnSiteApi.weAuthLogin()),
		);
		window.location.href = response.data.redirectUri;
	};

	const weAuthRequestOtp = async () => {
		setIsLoginPending(true);
		let response: Response<OtpVerifyToken> | null = null;
		try {
			response = new Response<OtpVerifyToken>(
				await HttpClient.request(OnSiteApi.weAuthRequestOtp()),
			);
			if (response?.data.verifyToken) {
				setOtpVerifyToken(response.data.verifyToken);
			}
		} finally {
			setIsLoginPending(false);
		}
		return response;
	};

	const verifyOtp = async (type: LoginType, otpCode: string) => {
		setIsLoginPending(true);
		try {
			if (otpVerifyToken === null) {
				throw new Error("OTP verify token is Empty.");
			}
			const response = new Response(
				await HttpClient.request(OnSiteApi.verifyOtp(otpCode, otpVerifyToken)),
			);
			setOtpVerifyToken(null);
			const isSecurityPledgeRequired =
				response.data.constraints.securityPledgeRequired;
			const session = response.data.sessionToken;
			SetSessionToken(session);
			setUser(new AdminUser(session));
			return isSecurityPledgeRequired;
		} finally {
			setIsLoginPending(false);
		}
	};

	const backstageLogin = async (
		backstageSession: string,
		artistId: number,
		partnerId: number,
	) => {
		setIsLoginPending(true);
		try {
			if (
				!backstageSession ||
				!Number.isInteger(artistId) ||
				!Number.isInteger(partnerId)
			) {
				throw new Error("Backstage login parameters are invalid.");
			}
			const response = new Response(
				await HttpClient.request(
					OnSiteApi.backstageLogin({
						token: backstageSession,
						artistId,
						partnerId,
					}),
				),
			);
			setOtpVerifyToken(null);
			const session = response.data.sessionToken;
			SetSessionToken(session);
			const admin = new AdminUser(session);
			setUser(admin);
			return admin;
		} finally {
			setIsLoginPending(false);
		}
	};

	return {
		oneDayLogin,
		weAuthLogin,
		weAuthRequestOtp,
		isLoginPending,
		verifyOtp,
		backstageLogin,
	};
}
